import { Injectable, inject } from '@angular/core';
import { LocationService } from './location.service';
import { WidgetService } from './widget.service';
import { VehicleService } from '../vehicle/vehicle.service';
import { Position } from '../../models/position';
import { AnalyticsConsentService } from './analytics-consent.service';

/**
 * JavaScript API of the widget (widget host) for the embedding site.
 *
 * Registered on `window` (and answered via postMessage for iframe embeddings) once
 * MainComponent starts in widget mode:
 *
 *   window.evWidget.gpsPositionUpdate(lat, lng, speed?, heading?, accuracy?, altitude?, timestamp?)
 *   window.evWidget.gpsPositionUpdate({ lat, lng | lon, speed?, heading?, accuracy?, altitude?, timestamp? })
 *   window.gpsPositionUpdate(...)                      alias of the above
 *   window.evWidget.setBattery(level)                  state of charge, 1..100
 *   window.evWidget.setAnalyticsConsent(granted)       Google Analytics consent (true / false)
 *
 *   iframe.contentWindow.postMessage({ type: 'gpsPositionUpdate', lat, lng, speed?, heading?, ... }, '*')
 *   iframe.contentWindow.postMessage({ type: 'setBattery', level }, '*')
 *   iframe.contentWindow.postMessage({ type: 'setAnalyticsConsent', granted }, '*')
 *
 * Speed is m/s, heading degrees, accuracy/altitude metres, timestamp ms since epoch.
 */
export interface WidgetGpsPosition {
  lat: number;
  lng?: number;
  lon?: number;
  speed?: number;
  heading?: number;
  accuracy?: number;
  altitude?: number;
  timestamp?: number;
}

@Injectable({ providedIn: 'root' })
export class WidgetApiService {

  private readonly widgetService = inject(WidgetService);
  private readonly locationService = inject(LocationService);
  private readonly vehicleService = inject(VehicleService);
  private readonly analyticsConsentService = inject(AnalyticsConsentService);
  private initialized = false;

  public init(): void {
    if (this.initialized || !this.widgetService.isWidget()) return;
    this.initialized = true;

    const api = {
      gpsPositionUpdate: (...args: any[]) => this.gpsPositionUpdate(...args),
      setBattery: (level: number) => this.setBattery(level),
      setAnalyticsConsent: (granted: boolean) => this.setAnalyticsConsent(granted),
    };
    (globalThis as any)['evWidget'] = api;
    (globalThis as any)['gpsPositionUpdate'] = api.gpsPositionUpdate;

    window.addEventListener('message', (event: MessageEvent) => {
      // Only the embedding page (or the widget's own origin) may drive the widget; other
      // frames on the host page (ads, third-party iframes) must not spoof GPS or battery.
      if (event.source !== globalThis.parent && event.origin !== globalThis.location.origin) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'gpsPositionUpdate') {
        this.gpsPositionUpdate(data);
      } else if (data.type === 'setBattery') {
        this.setBattery(data.level);
      } else if (data.type === 'setAnalyticsConsent') {
        this.setAnalyticsConsent(data.granted);
      }
    });
  }

  /** Accepts either positional (lat, lng, speed, heading, accuracy, altitude, timestamp) or one object. */
  public gpsPositionUpdate(...args: any[]): boolean {
    let p: WidgetGpsPosition;
    if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
      p = args[0];
    } else {
      const [lat, lng, speed, heading, accuracy, altitude, timestamp] = args;
      p = { lat, lng, speed, heading, accuracy, altitude, timestamp };
    }
    const lat = Number(p.lat);
    const lng = Number(p.lng ?? p.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      return false;
    }
    const num = (v: any, fallback: number) => (Number.isFinite(Number(v)) && v != null ? Number(v) : fallback);
    this.locationService.setLastPosition(new Position({
      latitude: lat, longitude: lng,
      speed: num(p.speed, 0), accuracy: num(p.accuracy, 0), altitude: num(p.altitude, 0), altitudeAccuracy: 0,
      heading: num(p.heading, 0), timestamp: num(p.timestamp, Date.now()),
    }));
    return true;
  }

  public setBattery(level: number): boolean {
    const value = Number(level);
    if (!Number.isFinite(value)) return false;
    const clamped = Math.min(100, Math.max(1, value));
    this.vehicleService.setBattery(clamped);
    // Moves the vehicle panel slider (and persists the level like a user change would).
    this.vehicleService.ObservableBatteryChanged.next(clamped);
    return true;
  }

  /** Only a real boolean counts; withdrawing (false) stops Google Analytics and deletes its cookies. */
  public setAnalyticsConsent(granted: boolean): boolean {
    if (typeof granted !== 'boolean') return false;
    this.analyticsConsentService.setConsent(granted);
    return true;
  }
}
